/* eslint-disable @repo/internal/deprecations/deprecation-ticket-required -- VOLTC-139 tracks removal of these deprecated re-export shims. */

import React, { useLayoutEffect } from 'react';

import { type LoadingMetricContentType } from './perf.config';
import { Provider } from './Provider';
import { usePerformanceMetrics } from './usePerformanceMetrics';

/**
 * @deprecated Use `import { usePerformanceMetrics } from '@atlassian/embedded-confluence-common/use-performance-metrics'` instead.
 */
export { usePerformanceMetrics } from './usePerformanceMetrics';

type KeyName = 'lazy-load' | 'common-load' | 'iframe-content-initialized';

export type MarkName = `${KeyName}-${'start' | 'end'}`;

/**
 * Performance metrics provider that manages provider initialization in a hierarchical component tree.
 *
 * Key features:
 * - Ensures single initialization: Only the topmost provider in the component tree becomes active
 * - Handles content type updates: Child components can update metrics via setContentType
 * - Prevents duplicate metrics: Automatically skips initialization if a parent provider exists
 *
 * Example: When ViewPage is rendered within Page component, provider is only initialized by Page
 */
export const PerformanceMetricsProvider = ({
	children,
	contentType,
	sourceComponent,
}: {
	children: React.ReactNode;
	contentType?: LoadingMetricContentType;
	sourceComponent?: string;
}): React.JSX.Element => {
	const { isInitialized, setContentType } = usePerformanceMetrics();
	useLayoutEffect(() => {
		// Update contentType when Page component replaces ViewPage with EditPage or vice versa
		isInitialized && contentType && setContentType(contentType);
	}, [isInitialized, contentType, setContentType]);
	return !isInitialized ? (
		<Provider contentType={contentType} sourceComponent={sourceComponent}>
			{children}
		</Provider>
	) : (
		<>{children}</>
	);
};

/**
 * @deprecated Use `import { PerformanceMetricsContext } from '@atlassian/embedded-confluence-common/performance-metrics-context-2'` instead.
 */
export { PerformanceMetricsContext } from './PerformanceMetricsContext-2';

/**
 * @deprecated Use `import { adjustedPerformanceTimestamp } from '@atlassian/embedded-confluence-common/adjusted-performance-timestamp'` instead.
 */
export { adjustedPerformanceTimestamp } from './adjustedPerformanceTimestamp';
